// @ts-strict-ignore
import React, { useEffect, useMemo } from 'react'
import { UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, type RadioOption } from '@node-space/ui-components'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  PaymentMethodLabels,
  PaymentMethods,
} from 'types/beneficiaries'
import {
  SEPA_METHODS,
  SEPA_CREDIT_INSTANT_TRANSFER_LIMIT
} from '../../constants'
import { FiatSendFormData } from '../../form/fiatSendFormSchema'
import { getAvailablePaymentMethods } from '../../utils'
import { PAYMENT_METHODS_LABELS } from 'pages/Contacts/constants'
import { PaymentMethodGroup } from './PaymentMethodGroup'
import { AchSameDayCheckbox } from './AchSameDayCheckbox'

const PaymentMethodDescriptionKeys = {
  [PaymentMethods.SEPA]: 'wallets.fiatSend.paymentMethodDescriptions.SEPA_CT',
  [PaymentMethods.SEPA_INSTANT]: 'wallets.fiatSend.paymentMethodDescriptions.SEPA_INST',
  [PaymentMethods.FASTER_PAYMENT]: 'wallets.fiatSend.paymentMethodDescriptions.FASTER_PAYMENT',
  [PaymentMethods.ACH]: 'wallets.fiatSend.paymentMethodDescriptions.ACH',
  [PaymentMethods.ACH_SAME_DAY]: 'wallets.fiatSend.paymentMethodDescriptions.ACH_SAME_DAY',
  [PaymentMethods.FEDWIRE]: 'wallets.fiatSend.paymentMethodDescriptions.FEDWIRE',
  [PaymentMethods.FEDNOW]: 'wallets.fiatSend.paymentMethodDescriptions.FEDNOW',
  [PaymentMethods.CRNOW]: 'wallets.fiatSend.paymentMethodDescriptions.CRNOW',
  [PaymentMethods.RTP]: 'wallets.fiatSend.paymentMethodDescriptions.RTP',
  [PaymentMethods.SWIFT]: 'wallets.fiatSend.paymentMethodDescriptions.SWIFT',
}

interface PaymentMethodSelectionProps {
  formValues: FiatSendFormData
  walletTransferDestinations: BeneficiaryTransferDestination[]
  bankAccountPaymentMethods: string[]
  currency: string
  amount?: string
  error?: string
  register: UseFormRegister<FiatSendFormData>
  setValue: UseFormSetValue<FiatSendFormData>
}

export const PaymentMethodSelection = ({
  formValues,
  walletTransferDestinations,
  bankAccountPaymentMethods,
  currency,
  amount,
  error,
  register,
  setValue,
}: PaymentMethodSelectionProps) => {
  const { t } = useTranslation()

  const availablePaymentMethods = useMemo(
    () =>
      getAvailablePaymentMethods(currency, walletTransferDestinations, bankAccountPaymentMethods),
    [walletTransferDestinations, bankAccountPaymentMethods, currency]
  )

  const paymentMethodOptions: RadioOption[] = useMemo(() => {
    const paymentMethodOptions: RadioOption[] = []

    switch (currency) {
      case BeneficiaryDetailsCurrency.EUR: {
        const hasSepa = availablePaymentMethods.some(method => SEPA_METHODS.has(method))
        const hasSwift = availablePaymentMethods.includes(PaymentMethods.SWIFT)

        if (hasSepa) {
          paymentMethodOptions.push(
            {
              label: `${t('wallets.fiatSend.autoPaymentMethod')} (${t('wallets.fiatSend.recommended')})`,
              value: '',
              secondaryLabel: t('wallets.fiatSend.autoPaymentMethodDescription'),
            },
            {
              label: t('wallets.fiatSend.sepaInstant'),
              value: PaymentMethods.SEPA_INSTANT,
              secondaryLabel: t(PaymentMethodDescriptionKeys[PaymentMethods.SEPA_INSTANT] || ''),
              disabled: Number(amount) > SEPA_CREDIT_INSTANT_TRANSFER_LIMIT,
            },
            {
              label: t('wallets.fiatSend.sepaCredit'),
              value: PaymentMethods.SEPA,
              secondaryLabel: t(PaymentMethodDescriptionKeys[PaymentMethods.SEPA] || ''),
            }
          )
        }

        if (hasSwift) {
          paymentMethodOptions.push({
            label: t('wallets.fiatSend.swift'),
            value: PaymentMethods.SWIFT,
            secondaryLabel: t(PaymentMethodDescriptionKeys[PaymentMethods.SWIFT] || ''),
          })
        }
        break
      }
      case BeneficiaryDetailsCurrency.USD: {
        const usdOptions = availablePaymentMethods.map(method => ({
          label: t(PAYMENT_METHODS_LABELS[method] ?? method),
          secondaryLabel: t(PaymentMethodDescriptionKeys[method] ?? ''),
          value: method,
        }))
        paymentMethodOptions.push(...usdOptions)
        break
      }
      case BeneficiaryDetailsCurrency.GBP: 
        paymentMethodOptions.push({
          label: t('paymentMethodsLabels.fasterPayment'),
          value: PaymentMethods.FASTER_PAYMENT,
          secondaryLabel: t(PaymentMethodDescriptionKeys[PaymentMethods.FASTER_PAYMENT] || ''),
        })
      
      break
      default:
    }
     
    return paymentMethodOptions
  }, [availablePaymentMethods, amount, currency, t, walletTransferDestinations])

  useEffect(() => {
    const isSingleOption = paymentMethodOptions?.length === 1
    const isSwiftAvailable = paymentMethodOptions?.some(option => option.value === PaymentMethods.SWIFT)
    const isSwiftOnly = isSwiftAvailable && isSingleOption  
    const isPaymentMethodSet = !!formValues?.paymentMethod

    if (isSwiftOnly) {
      setValue('paymentMethod', PaymentMethods.SWIFT)
      return
    } 

    // Set if only one option
    if (isSingleOption) {
      setValue('paymentMethod', paymentMethodOptions?.[0]?.value ?? '')
      return
    }

    if (!isSingleOption && !isPaymentMethodSet) {
      setValue('paymentMethod', paymentMethodOptions?.[0]?.value ?? '')
    }
  }, [paymentMethodOptions, currency, formValues?.paymentMethod, walletTransferDestinations, setValue])

  if (!paymentMethodOptions?.length) return null

  return (
    <>
      {paymentMethodOptions?.length === 1 ?
        <DataListItem 
          layout="vertical" 
          id="payment-method-item" 
          title={t('paymentMethod')} 
          body={PaymentMethodLabels[formValues?.paymentMethod] || formValues?.paymentMethod}  
          />
      : (
        <PaymentMethodGroup formValues={formValues} paymentMethodOptions={paymentMethodOptions} error={error} setValue={setValue} />
      )}
      
      {formValues?.paymentMethod === PaymentMethods.ACH && (
        <Box paddingT={12} paddingB={8}>
          <AchSameDayCheckbox formValues={formValues} register={register} setValue={setValue} />
        </Box>
      )}
    </>
  )
}
